import { computed, type FunctionalComponent } from 'vue'
import { useFormWizard, type FormWizardHandler } from 'components/form-wizard/hooks/useFormWizard'
import { popModal } from 'components/modal/store'
import { useKeyboard } from '@/utils/hooks/useKeyboard'
import type { MaybeRef, Ref } from 'vue'
import type { IntegrationProvider, IProviderImport } from 'findef'
import {
  EProviderImportEvent,
  useProviderImport,
  type UseProviderImport
} from '@/modules/providerImport/hooks/useProviderImport'
import { useProviderImportSteps } from '@/modules/providerImport/hooks/useProviderImportSteps'
import { EProviderImportStep } from '@/modules/providerImport/types/steps'
import type { IProviderImportError } from '@/modules/providerImport/hooks/types'
import { refreshInvestments, refreshProviderImports } from '~/composables/useDataRefresh'

type ProviderImport = Omit<IProviderImport, 'provider'> & { provider: IntegrationProvider }

export type UseImportAssets = {
  wizard: FormWizardHandler
  //manage: UseImportAssetsForm
  errors: Ref<Array<IProviderImportError>>
  reset: () => void;
  providerImportHook: UseProviderImport;
  __v_skip: true
}

export type UseImportAssetsProps = {
  onCancelOverride?: () => any
  onGoBack?: (index: number) => Promise<void>
  onSave?: () => any
  onLastGoBack?: (index: number) => void
  breadcrumbs?: boolean;
  title?: string;
  step?: MaybeRef<number>
  providerImport?: ProviderImport
  selectProviderComponent?: FunctionalComponent<{
    handleSelectProviderImport: (imp: IProviderImport) => any
  }>
  hideFirstGoBack?: boolean
  hideBackButtonOnIndex?: number
  noTopPadding?: boolean
  wrapperClass?: string
}


export const useImportAssets = (props: UseImportAssetsProps = {}): UseImportAssets => {
  const providerImportHook = useProviderImport({})
  const providerImportSteps = useProviderImportSteps({
    hook: providerImportHook,
    selectProviderComponent: props.selectProviderComponent,
    showCrumbs: props.breadcrumbs === undefined ? true : props.breadcrumbs,
    title: props.title
  })

  if (props.providerImport) {
    providerImportHook.selectProvider(props.providerImport.provider.id)
  }

  providerImportHook.events.subscribe(EProviderImportEvent.IMPORT_FETCHED, (_ev) => {
    providerImportSteps.nextStep()
  })

  const wizard = useFormWizard({
    globalTitle: props.title,
    steps: [
      {
        title: 'Select Provider',
        body: () => providerImportSteps.currentContent.value
      }
    ],
    showFooter: true,
    noTopSection: true,
    noTopPadding: true,
    nextButtonText: computed(() => {
      switch (providerImportSteps.step.value) {
        case EProviderImportStep.SELECT_PROVIDER:
          return 'Next'
        case EProviderImportStep.AUTHENTICATE:
          return 'Next'
        case EProviderImportStep.SELECT_POSITIONS:
          return 'Save'
        default:
          return 'Next'
      }
    }),
    onClickNext: async () => {
      switch (providerImportSteps.step.value) {
        case EProviderImportStep.SELECT_PROVIDER:
          return
        case EProviderImportStep.AUTHENTICATE:
          {
            const state = providerImportHook.form.cast()
            if (!state.userId || state.userId.length < 12) return
            if (
              providerImportHook.userAccounts.value.length > 1 &&
              providerImportHook.form.state.value.userAccountId
            ) {
              await providerImportHook.selectUserAccount(
                providerImportHook.form.state.value.userAccountId
              )
              return
            }
            providerImportHook.scanQR(state.userId)
          }
          break
        case EProviderImportStep.CONNECTING:
          return
        case EProviderImportStep.SELECT_POSITIONS: {
          providerImportSteps.setStep(EProviderImportStep.SAVING)
          await providerImportHook.save()
          refreshInvestments()
          refreshProviderImports()

          if (props.onSave) {
            await props.onSave()
          }
          popModal()
          return
        }
        default:
          return
      }
    },
    nextButtonDisabled: computed(() => {
      if (providerImportHook.isScanningQR.value) return true;
      switch (providerImportSteps.step.value) {
        case EProviderImportStep.SELECT_PROVIDER:
          return true
        case EProviderImportStep.AUTHENTICATE:
          {
            const state = providerImportHook.form.cast()
            if (!state.userId || state.userId.length < 12) return true
            if (providerImportHook.form.errors.value.length > 0) return true
            if (
              providerImportHook.userAccounts.value.length > 0 &&
              !providerImportHook.form.state.value.userAccountId
            )
              return true
          }
          break
        case EProviderImportStep.CONNECTING:
          return true
        case EProviderImportStep.SELECT_POSITIONS:
          return false
        default:
          return false
      }
      return false
    }),
    onGoBack: async () => {
      switch (providerImportSteps.step.value) {
        case EProviderImportStep.SELECT_PROVIDER: {
          if (props.onLastGoBack) {
            await reset();
            props.onLastGoBack(0);
          }
        }; break;
        default: {
          await reset();
          providerImportSteps.setStep(EProviderImportStep.SELECT_PROVIDER);
        }; break;
      }
    },
    hideGoBack: computed(() => {
      switch (providerImportSteps.step.value) {
          case EProviderImportStep.SELECT_PROVIDER: return !!props.selectProviderComponent;
      }
      return false;
    }),
    onCancel: () => {
      providerImportHook.cancel();

      if (props.onCancelOverride) {
        props.onCancelOverride();
      } else {
        popModal();
      }
    },
  })

  const keyboard = useKeyboard()
  keyboard.addEventListener('keypress', (ev) => {
    if (ev.key === 'Enter') {
      if (
        providerImportSteps.step.value === EProviderImportStep.AUTHENTICATE &&
        providerImportHook.form.errors.value.length <= 0 &&
        providerImportHook.form.state.value.userId &&
        !providerImportHook.isScanningQR.value &&
        !providerImportHook.qrString.value
      ) {
        providerImportHook.scanQR(providerImportHook.form.state.value.userId)
      }
    }
  });

  const reset = async () => {
    await providerImportHook.reset();
  }

  return {
    wizard,
    reset,
    errors: providerImportHook.errors,
    providerImportHook,
    __v_skip: true
  }
}
